/**
 * Karbon360° v2 — Facilities.
 *
 * The sites inside the reporting boundary. Floor area drives every intensity
 * metric, so the screen surfaces missing area rather than quietly reporting a
 * lower intensity than reality.
 *
 * LANGUAGE follows the design spec: English on the analysis surface (table,
 * headings, coverage), Albanian on the data-entry form — "Ndërtesat" is named in
 * v2/CLAUDE.md as an Albanian surface.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ErrorBoundary from '../components/ErrorBoundary';
import { Shell } from '../components/Shell';
import { X } from '../components/v2/Icons';
import {
  PageHeader, Card, Button, Stat, Badge, Callout, Empty, Skeleton,
  Field, Input, Select, Drawer, Modal, DList, Sect, TableWrap, BarRow,
  useToast, fmt,
} from '../components/v2/ui';
import { useT } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const n = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v));

const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const BUILDING_TYPES = ['Zyre', 'Depo / Magazinë', 'Prodhim', 'Shitje me pakicë', 'Qendër të dhënash', 'Tjetër'];
// Stored values stay Albanian (sent to the API as-is); only the visible labels translate.
const BUILDING_TYPE_LABEL_KEYS = {
  'Zyre': 'facp.typeOffice',
  'Depo / Magazinë': 'facp.typeWarehouse',
  'Prodhim': 'facp.typeProduction',
  'Shitje me pakicë': 'facp.typeRetail',
  'Qendër të dhënash': 'facp.typeDataCenter',
  'Tjetër': 'facp.typeOther',
};

const EMPTY = {
  name: '', building_type: '', address: '', city: '', country: 'Kosovë',
  construction_year: '', floors: '', area_sqm: '',
  energy_sources: '', water_supply: '', heating_cooling: '',
};

function FacilitiesScreen() {
  const { t } = useT();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [buildings, setBuildings] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [open, setOpen] = useState(null);      // drawer
  const [editing, setEditing] = useState(null); // modal: {} = new, {..} = edit
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = () => {
    setLoading(true);
    const y = new Date().getFullYear();
    Promise.all([
      axios.get(`${API_URL}/api/buildings`, auth()).then((r) => (Array.isArray(r.data) ? r.data : r.data?.buildings || [])),
      axios.get(`${API_URL}/api/emissions/coverage/matrix?year=${y}`, auth()).then((r) => r.data).catch(() => null),
    ])
      .then(([b, m]) => { setBuildings(b); setMatrix(m); })
      .catch((e) => setError(e.response?.data?.message || 'Could not load your facilities.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const stat = useMemo(() => {
    const m = new Map((matrix?.sites || []).map((s) => [String(s.id), s]));
    return (id) => m.get(String(id)) || null;
  }, [matrix]);

  const totals = useMemo(() => {
    const area = buildings.reduce((s, b) => s + n(b.area_sqm), 0);
    const tco2e = (matrix?.sites || []).reduce((s, x) => s + n(x.tco2e), 0);
    const covered = (matrix?.sites || []).reduce((s, x) => s + x.covered, 0);
    const siteMonths = (matrix?.sites || []).length * 12;
    const noArea = buildings.filter((b) => !n(b.area_sqm)).length;
    return {
      area, tco2e, noArea,
      intensity: area > 0 ? (tco2e * 1000) / area : null,
      coveragePct: siteMonths ? Math.round((covered / siteMonths) * 100) : 0,
    };
  }, [buildings, matrix]);

  const startNew = () => { setForm(EMPTY); setFormError(''); setEditing({}); };
  const startEdit = (b) => {
    setForm({
      name: b.name || '', building_type: b.building_type || '', address: b.address || '',
      city: b.city || '', country: b.country || 'Kosovë',
      construction_year: b.construction_year || '', floors: b.floors || '', area_sqm: b.area_sqm || '',
      energy_sources: b.energy_sources || '', water_supply: b.water_supply || '', heating_cooling: b.heating_cooling || '',
    });
    setFormError('');
    setEditing(b);
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) { setFormError(t('facp.nameRequired')); return; }
    setSaving(true); setFormError('');
    const payload = {
      ...form,
      construction_year: form.construction_year === '' ? null : Number(form.construction_year),
      floors: form.floors === '' ? null : Number(form.floors),
      area_sqm: form.area_sqm === '' ? null : Number(form.area_sqm),
    };
    try {
      if (editing?.id) {
        await axios.put(`${API_URL}/api/buildings/${editing.id}`, payload, auth());
        toast(t('facp.updated'));
      } else {
        await axios.post(`${API_URL}/api/buildings`, payload, auth());
        toast(t('facp.added'));
      }
      setEditing(null); setOpen(null); load();
    } catch (err) {
      setFormError(err.response?.data?.message || t('facp.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    const s = stat(b.id);
    const warn = s && s.covered > 0
      ? `${b.name} has ${s.covered} month${s.covered > 1 ? 's' : ''} of recorded data. Deleting the facility does not delete those records, but they will no longer be attributed to a site.\n\nDelete anyway?`
      : `Delete ${b.name}?`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(warn)) return;
    try {
      await axios.delete(`${API_URL}/api/buildings/${b.id}`, auth());
      toast(t('facp.deleted'));
      setOpen(null); load();
    } catch (err) {
      toast(err.response?.data?.message || t('facp.deleteFailed'), { tone: 'err' });
    }
  };

  if (error) {
    return <div className="k2-wrap"><PageHeader title={t('fac.title')} /><Callout tone="err">{error}</Callout></div>;
  }

  return (
    <div className="k2-wrap">
      <PageHeader
        title={t('fac.title')}
        desc="Sites in your reporting boundary. Floor area and use type drive every intensity metric, so keep them current."
      >
        <Button icon={X.Plus} variant="mint" onClick={startNew}>{t('facp.addFacility')}</Button>
      </PageHeader>

      {totals.noArea > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">
            <b>{totals.noArea} facilit{totals.noArea > 1 ? 'ies have' : 'y has'} no floor area recorded.</b>{' '}
            Intensity is calculated across the area that IS recorded, so it currently reads higher per m² than reality.
          </Callout>
        </div>
      )}

      <div className="k2-g4" style={{ marginBottom: 24 }}>
        <Stat tone="dark" label={t('fac.inBoundary')} value={buildings.length} foot="Operational control" />
        <Stat label={t('fac.totalArea')} value={fmt(totals.area, 0)} unit="m²" foot={totals.noArea ? `${totals.noArea} missing area` : 'All recorded'} />
        <Stat label={t('fac.avgIntensity')} value={totals.intensity === null ? '—' : fmt(totals.intensity, 1)} unit={totals.intensity === null ? '' : 'kgCO₂e/m²'} foot={`${fmt(totals.tco2e, 2)} tCO₂e this year`} />
        <Stat label={t('fac.coverage')} value={totals.coveragePct} unit="%" foot="Target 95% before filing" />
      </div>

      {loading ? (
        <Card><div style={{ display: 'grid', gap: 10 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={30} />)}</div></Card>
      ) : buildings.length === 0 ? (
        <Card>
          <Empty
            icon={X.Building}
            title={t('fac.empty')}
            body={t('fac.emptyBody')}
            action={<Button variant="mint" icon={X.Plus} onClick={startNew}>{t('facp.addFacility')}</Button>}
          />
        </Card>
      ) : (
        <TableWrap
          toolbar={<span style={{ fontSize: 12.5, color: 'var(--n500)' }}>{buildings.length} sites · operational-control boundary</span>}
          scroll={false}
        >
          <table className="k2-t">
            <thead>
              <tr>
                <th>Facility</th>
                <th>Type</th>
                <th className="k2-r">Floor area</th>
                <th className="k2-r">Floors</th>
                <th className="k2-r">Built</th>
                <th className="k2-r">Emissions</th>
                <th>Coverage</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {buildings.map((b) => {
                const s = stat(b.id);
                const cov = s ? Math.round((s.covered / 12) * 100) : 0;
                return (
                  <tr key={b.id} onClick={() => setOpen(b)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="k2-cellflex">
                        <span className="k2-tsq k2-mint"><X.Building s={14} /></span>
                        <span style={{ minWidth: 0 }}>
                          <span className="k2-nm">{b.name}</span>
                          <span className="k2-sub2">{[b.city, b.country].filter(Boolean).join(', ') || '—'}</span>
                        </span>
                      </div>
                    </td>
                    <td>{b.building_type ? <Badge tone="out">{b.building_type}</Badge> : <span style={{ color: 'var(--n400)' }}>—</span>}</td>
                    <td className="k2-r k2-num">{n(b.area_sqm) ? `${fmt(n(b.area_sqm), 0)} m²` : <span style={{ color: 'var(--warn)' }}>not set</span>}</td>
                    <td className="k2-r k2-num">{b.floors ?? '—'}</td>
                    <td className="k2-r k2-num" style={{ color: 'var(--n500)' }}>{b.construction_year ?? '—'}</td>
                    <td className="k2-r k2-num" style={{ fontWeight: 600 }}>
                      {s ? fmt(s.tco2e, 3) : '0.000'} <span style={{ fontSize: 11, color: 'var(--n400)', fontWeight: 400 }}>tCO₂e</span>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 44, height: 5, borderRadius: 99, background: 'var(--n100)', overflow: 'hidden', display: 'block' }}>
                          <span style={{ display: 'block', height: '100%', width: `${cov}%`, background: cov > 80 ? 'var(--ok)' : cov > 40 ? 'var(--brand-700)' : 'var(--warn)' }} />
                        </span>
                        <span className="k2-num" style={{ fontSize: 11.5, color: 'var(--n500)', minWidth: 30 }}>{cov}%</span>
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="k2-rowact">
                        <Button size="sm" variant="gh" iconOnly icon={X.Gear} aria-label={`Edit ${b.name}`} onClick={() => startEdit(b)} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}

      {open && (
        <Drawer
          title={open.name}
          sub={[open.city, open.country].filter(Boolean).join(', ') || '—'}
          badge={open.building_type ? <Badge tone="out" lg>{open.building_type}</Badge> : null}
          onClose={() => setOpen(null)}
          footer={
            <>
              <Button variant="dgr" icon={X.Close} onClick={() => remove(open)} style={{ marginRight: 'auto' }}>{t('facp.delete')}</Button>
              <Button onClick={() => setOpen(null)}>Close</Button>
              <Button variant="pri" icon={X.Gear} onClick={() => startEdit(open)}>{t('facp.edit')}</Button>
            </>
          }
        >
          <Sect title={t('fac.profile')}>
            <DList rows={[
              ['Floor area', n(open.area_sqm) ? `${fmt(n(open.area_sqm), 0)} m²` : 'Not recorded'],
              ['Floors', open.floors ?? '—'],
              ['Year built', open.construction_year ?? '—'],
              ['Use type', open.building_type || '—'],
              ['Address', open.address || '—'],
              ['Energy sources', open.energy_sources || '—'],
              ['Heating & cooling', open.heating_cooling || '—'],
              ['Water supply', open.water_supply || '—'],
              ['Boundary', 'Operational control'],
            ]} />
          </Sect>

          {(() => {
            const s = stat(open.id);
            const area = n(open.area_sqm);
            return (
              <>
                <Sect title={`Emissions — ${matrix?.year ?? ''}`}>
                  <div className="k2-g2" style={{ marginBottom: 12 }}>
                    <Stat label={t('fac.total')} value={s ? fmt(s.tco2e, 3) : '0.000'} unit="tCO₂e" />
                    <Stat
                      label={t('fac.intensity')}
                      value={area && s ? fmt((s.tco2e * 1000) / area, 1) : '—'}
                      unit={area && s ? 'kg/m²' : ''}
                      foot={area ? '' : 'Needs floor area'}
                    />
                  </div>
                  {!s && <Callout tone="info">No records attached to this facility yet.</Callout>}
                </Sect>

                {s && (
                  <Sect title={t('fac.coverage')}>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {s.months.map((m) => (
                        <div
                          key={m.month}
                          title={`${MONTHS[m.month - 1]} — ${m.state === 'complete' ? 'Scope 1 & 2 recorded' : m.state === 'partial' ? 'partial' : 'no data'}`}
                          style={{
                            flex: 1, height: 22, borderRadius: 4,
                            background: m.state === 'complete' ? 'var(--brand-700)' : m.state === 'partial' ? 'var(--brand-100)' : 'var(--n50)',
                            border: m.state === 'none' ? '1px dashed var(--n300)' : '1px solid transparent',
                          }}
                        />
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
                      {MONTHS.map((m, i) => (
                        <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9.5, color: 'var(--n400)' }}>{m}</div>
                      ))}
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <BarRow name="Months covered" value={String(s.covered)} unit="/ 12" pct={(s.covered / 12) * 100}
                              color={s.covered > 9 ? 'var(--ok)' : 'var(--brand-700)'} />
                      <BarRow name="Scope 1 & 2 complete" value={String(s.complete)} unit="/ 12" pct={(s.complete / 12) * 100}
                              color={s.complete > 9 ? 'var(--ok)' : 'var(--warn)'} />
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <Button size="sm" icon={X.Plus} onClick={() => navigate('/regjistrimi')}>{t('facp.addData')}</Button>
                    </div>
                  </Sect>
                )}
              </>
            );
          })()}
        </Drawer>
      )}

      {editing && (
        <Modal
          title={editing.id ? t('facp.editFacility') : t('facp.addFacility')}
          sub={t('fac.sub')}
          wide
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button onClick={() => setEditing(null)}>{t('facp.cancel')}</Button>
              <Button variant="mint" icon={X.Check} onClick={save} disabled={saving}>
                {saving ? t('facp.saving') : editing.id ? t('facp.saveChanges') : t('facp.addFacilitySubmit')}
              </Button>
            </>
          }
        >
          <form onSubmit={save}>
            {formError && <div style={{ marginBottom: 14 }}><Callout tone="err">{formError}</Callout></div>}
            <div className="k2-g2">
              <Field label={t('facp.name')} required>
                <Input value={form.name} onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))} placeholder={t('facp.namePlaceholder')} autoFocus />
              </Field>
              <Field label={t('facp.buildingType')}>
                <Select value={form.building_type} onChange={(e) => setForm((v) => ({ ...v, building_type: e.target.value }))}>
                  <option value="">{t('facp.selectType')}</option>
                  {BUILDING_TYPES.map((bt) => <option key={bt} value={bt}>{t(BUILDING_TYPE_LABEL_KEYS[bt])}</option>)}
                </Select>
              </Field>
              <Field label={t('facp.address')}>
                <Input value={form.address} onChange={(e) => setForm((v) => ({ ...v, address: e.target.value }))} />
              </Field>
              <Field label={t('facp.city')}>
                <Input value={form.city} onChange={(e) => setForm((v) => ({ ...v, city: e.target.value }))} />
              </Field>
              <Field label={t('facp.country')}>
                <Input value={form.country} onChange={(e) => setForm((v) => ({ ...v, country: e.target.value }))} />
              </Field>
              <Field label={t('facp.constructionYear')} optional>
                <Input type="number" value={form.construction_year} onChange={(e) => setForm((v) => ({ ...v, construction_year: e.target.value }))} placeholder="2016" />
              </Field>
              <Field label={t('facp.areaSqm')} hint={t('facp.areaHint')}>
                <Input type="number" value={form.area_sqm} onChange={(e) => setForm((v) => ({ ...v, area_sqm: e.target.value }))} placeholder="m²" />
              </Field>
              <Field label={t('facp.floors')} optional>
                <Input type="number" value={form.floors} onChange={(e) => setForm((v) => ({ ...v, floors: e.target.value }))} />
              </Field>
              <Field label={t('facp.energySources')}>
                <Input value={form.energy_sources} onChange={(e) => setForm((v) => ({ ...v, energy_sources: e.target.value }))} placeholder={t('facp.energyPlaceholder')} />
              </Field>
              <Field label={t('facp.heatingCooling')}>
                <Input value={form.heating_cooling} onChange={(e) => setForm((v) => ({ ...v, heating_cooling: e.target.value }))} />
              </Field>
              <Field label={t('facp.waterSupply')}>
                <Input value={form.water_supply} onChange={(e) => setForm((v) => ({ ...v, water_supply: e.target.value }))} />
              </Field>
            </div>
            <button type="submit" hidden aria-hidden="true" />
          </form>
        </Modal>
      )}
    </div>
  );
}

export default function FacilitiesPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <FacilitiesScreen />
      </Shell>
    </ErrorBoundary>
  );
}
